/**
 * context/appContext.tsx
 * App-level UI state that isn't the signed-in identity. Today that's the demo
 * "vertical" switcher in the sidebar — a presentation affordance proving the
 * one engine drives every vertical. Real tenancy is RLS-scoped per token; this
 * only selects which demo dataset the seed fallback serves and which category
 * vocabulary the builder shows when the live catalog is unavailable.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';
import { setDemoVertical } from '../services/_demo/verticalStore';

export type VerticalKey = 'restaurant' | 'fashion' | 'construction';

export interface VerticalMeta {
  key: VerticalKey;
  label: string;
  tenant: string;
  brand: string;
}

export const VERTICALS: Record<VerticalKey, VerticalMeta> = {
  restaurant: { key: 'restaurant', label: 'Restaurant', tenant: 'Spice Route Kitchen', brand: 'S' },
  fashion: { key: 'fashion', label: 'Dress shop', tenant: 'Métier Atelier', brand: 'M' },
  construction: { key: 'construction', label: 'Construction', tenant: 'BuildRight Interiors', brand: 'B' },
};

export const VERTICAL_OPTIONS = (Object.keys(VERTICALS) as VerticalKey[]).map((k) => ({
  value: k,
  label: `${VERTICALS[k].label} · ${VERTICALS[k].tenant}`,
}));

interface AppContextValue {
  vertical: VerticalKey;
  meta: VerticalMeta;
  setVertical: (v: VerticalKey) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [vertical, setVerticalState] = useState<VerticalKey>('restaurant');

  const value = useMemo<AppContextValue>(
    () => ({
      vertical,
      meta: VERTICALS[vertical],
      setVertical: (v: VerticalKey) => {
        setVerticalState(v);
        setDemoVertical(v); // keep the seed fallback in sync with the UI
      },
    }),
    [vertical],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
